import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retrieveFileOptions, invalidateFileQueries } from "./canonical-files";
import {
  downloadFile,
  downloadFileVersion,
} from "@/lib/data/requests/files/canonical-files";
import {
  saveEditedFile,
  saveEditedCopy,
  editorSaveUnavailableReason,
} from "@/lib/data/requests/files/editor-files";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { CanonicalFile } from "@/lib/data/schemas/files";

export function useFileEditor(fileId: string, open: boolean) {
  const client = useQueryClient();
  const metadata = useQuery(retrieveFileOptions(fileId, open));
  const [snapshot, setSnapshot] = useState<CanonicalFile>();
  const [pinnedSource, setPinnedSource] = useState<{
    fileId: string;
    url: string;
  }>();
  useEffect(() => {
    if (!open || (snapshot && snapshot.id !== fileId)) {
      setSnapshot(undefined);
      setPinnedSource(undefined);
    } else if (!snapshot && metadata.data && !metadata.isFetching) {
      setSnapshot(metadata.data);
    }
  }, [fileId, open, snapshot, metadata.data, metadata.isFetching]);
  const file = open && snapshot?.id === fileId ? snapshot : undefined;
  const source = useQuery({
    queryKey: [
      queryKeys.files,
      fileId,
      "editor-source",
      file?.currentVersionId,
      file?.revision,
    ],
    enabled: !!file,
    queryFn: async () => {
      if (!file) throw new Error("Unable to load this file.");
      return file.currentVersionId && file.capabilities.canUploadVersion
        ? downloadFileVersion(file.id, file.currentVersionId)
        : downloadFile(file.id);
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  useEffect(() => {
    // A refreshed signed URL makes Apryse reload and discard active edits.
    if (file && !pinnedSource && source.data?.url)
      setPinnedSource({ fileId: file.id, url: source.data.url });
  }, [file, pinnedSource, source.data]);
  const url =
    file && pinnedSource?.fileId === file.id ? pinnedSource.url : undefined;
  const save = useMutation({
    mutationFn: ({
      blob,
      contentType,
    }: {
      blob: Blob;
      contentType: string;
    }) => {
      if (!file) throw new Error("Wait for this file to finish loading.");
      return saveEditedFile(file, blob, contentType);
    },
    retry: false,
    onSuccess: () => invalidateFileQueries(client),
  });
  const copy = useMutation({
    mutationFn: (input: Parameters<typeof saveEditedCopy>[1]) => {
      if (!file) throw new Error("Wait for this file to finish loading.");
      return saveEditedCopy(file, input);
    },
    retry: false,
    onSuccess: () => invalidateFileQueries(client),
  });
  return {
    file,
    url,
    isLoading:
      !url &&
      ((!file && !metadata.isError) || source.isLoading || !!source.data),
    isError: !url && ((!file && metadata.isError) || source.isError),
    canSave: !!file && !editorSaveUnavailableReason(file),
    saveUnavailableReason: file ? editorSaveUnavailableReason(file) : undefined,
    save,
    copy,
  };
}
